import { useCallback, useEffect, useState } from 'react'
import { Spinner } from '@fluentui/react-components'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { filterByRoles, hasRequiredRole } from '@unified-governance/shared'
import type { RemoteRegistry } from '@unified-governance/shared'
import { useShellAuth } from './auth/ShellAuthContext'
import { initFederation, loadRemoteNavigation, loadRemoteResources, registerRemoteEntries } from './federation'
import { fetchRegistry } from './remotesRegistry'
import { Header } from './Header'
import { HomePage } from './components/Home/HomePage'
import { PageNotFound } from './components/PageNotFound/PageNotFound'
import { NavMenu } from './nav/NavMenu'
import { RemoteOutlet } from './RemoteOutlet'
import type { HelpSection, MenuSection } from './shell.types'
import type { ShellTelemetry } from './telemetry/shellTelemetry'
import './App.css'

interface AppProps {
  telemetry: ShellTelemetry
}

function App({ telemetry }: AppProps) {
  const auth = useShellAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [registry, setRegistry] = useState<RemoteRegistry>([])
  const [sections, setSections] = useState<MenuSection[]>([])
  const [registryStatus, setRegistryStatus] = useState<'loading' | 'ready' | 'failed'>('loading')
  const [navigationReady, setNavigationReady] = useState(false)
  const [helpSections, setHelpSections] = useState<HelpSection[]>([])
  const canRenderWorkspace = registryStatus === 'ready' && navigationReady
  const isLoading = registryStatus !== 'failed' && !navigationReady

  useEffect(() => {
    let active = true

    const bootstrap = async () => {
      initFederation()
      const entries = await fetchRegistry()
      if (!active) {
        return
      }
      registerRemoteEntries(entries)
      setRegistry(entries)
      setRegistryStatus('ready')

      const collected = new Map<string, MenuSection>()
      await Promise.all(entries.map(async (entry) => {
        const nav = await loadRemoteNavigation(entry.name, auth)
        if (!active) {
          return
        }
        if (!nav) {
          telemetry.event('remote.navigation.invalid', { remoteName: entry.name })
          return
        }

        collected.set(entry.name, {
          remoteName: entry.name,
          basePath: entry.basePath,
          appName: nav.appName,
          items: filterByRoles(nav.items, auth.roles),
          itemIconDataUrls: nav.itemIconDataUrls ?? {},
          groupIconDataUrls: nav.groupIconDataUrls ?? {},
        })
        setSections(
          entries.flatMap((registeredEntry) => {
            const section = collected.get(registeredEntry.name)
            return section ? [section] : []
          })
        )
        setNavigationReady(true)
      }))
      if (active) {
        setNavigationReady(true)
      }
    }

    bootstrap().catch((error) => {
      if (active) {
        console.warn('[shell] Application bootstrap failed', error)
        setRegistryStatus('failed')
      }
    })

    return () => {
      active = false
    }
  }, [telemetry, auth])

  useEffect(() => {
    if (isLoading) {
      return
    }

    let active = true

    const loadHelp = async () => {
      const collected = await Promise.all(sections.map(async (section): Promise<HelpSection[]> => {
        const resources = await loadRemoteResources(section.remoteName)
        if (!active || !resources) {
          return []
        }

        return resources.helpSections.flatMap((helpSection) => {
          const visibleLinks = helpSection.links.filter((link) =>
            hasRequiredRole(link.requiredRoles, auth.roles)
          )
          return visibleLinks.length > 0
            ? [{
                remoteName: section.remoteName,
                title: section.appName,
                links: visibleLinks,
              }]
            : []
        })
      }))
      if (active) {
        setHelpSections(collected.flat())
      }
    }

    loadHelp().catch((error) => {
      if (active) {
        console.warn('[shell] Remote help resources could not be loaded', error)
      }
    })

    return () => {
      active = false
    }
  }, [sections, auth.roles, isLoading])

  useEffect(() => {
    telemetry.setRoute(location.pathname)
    telemetry.event('route.change', { toRoute: location.pathname })
  }, [location.pathname, telemetry])

  const handleNavigate = useCallback(
    (routePath: string) => {
      navigate(routePath)
      window.dispatchEvent(new PopStateEvent('popstate'))
    },
    [navigate]
  )

  return (
    <div className="shell-root">
      <Header helpSections={helpSections} telemetry={telemetry} />
      <div
        className={`shell-body${isLoading ? ' shell-body-loading' : ''}`}
        aria-busy={isLoading}
      >
        {isLoading && (
          <div className="shell-loading">
            <Spinner className="shell-loading-spinner" label="Loading your workspace..." />
          </div>
        )}
        <aside className="shell-nav" aria-hidden={isLoading || undefined}>
          {canRenderWorkspace && (
            <NavMenu
              sections={sections}
              selectedPath={location.pathname}
              onNavigate={handleNavigate}
            />
          )}
        </aside>
        <main className="shell-content" aria-hidden={isLoading || undefined}>
          {registryStatus === 'failed' && (
            <div className="shell-status" role="status">Unable to load applications.</div>
          )}
          {canRenderWorkspace && (
            <Routes>
              <Route path="/" element={<HomePage />} />
              {registry.map((entry) => (
                <Route
                  key={entry.name}
                  path={`/${entry.basePath}/*`}
                  element={
                    <RemoteOutlet
                      key={entry.name}
                      entry={entry}
                      auth={auth}
                      telemetry={telemetry}
                    />
                  }
                />
              ))}
              <Route path="*" element={<PageNotFound />} />
            </Routes>
          )}
        </main>
      </div>
    </div>
  )
}

export default App
